/**
 * AlphaCore Design Tokens — v2.0
 *
 * Ported from: docs/AlphaCore-Web-handoff/alphacore-web/project/tokens.css
 * Four palettes: calm (default) | clinical | editorial | operator
 * Applied via data-theme attribute on <html> and CSS custom properties.
 * This file is the single source of truth for all token values.
 */

// ─────────────────────────────────────────────
// Theme identifiers
// ─────────────────────────────────────────────

export type ThemeName = "calm" | "clinical" | "editorial" | "operator";
export type DensityName = "compact" | "medium" | "comfortable";

// ─────────────────────────────────────────────
// Token shape
// ─────────────────────────────────────────────

export interface ColorTokens {
  // Surface
  bg: string;
  bgElev: string;
  bgSunken: string;
  bgRail: string;
  bgRailFg: string;
  bgRailFgActive: string;

  // Ink
  ink1: string;
  ink2: string;
  ink3: string;
  ink4: string;
  line: string;
  lineStrong: string;

  // Accent
  accent: string;
  accentInk: string;
  accentSoft: string;
  accentSoftInk: string;

  // Semantic — ok
  ok: string;
  okSoft: string;
  okSoftInk: string;

  // Semantic — warn
  warn: string;
  warnSoft: string;
  warnSoftInk: string;

  // Semantic — risk
  risk: string;
  riskSoft: string;
  riskSoftInk: string;

  // Solid pill ink (paired with --ok / --risk backgrounds, AA-verified)
  okInk: string;
  riskInk: string;

  // Solid pill ink for --warn surfaces (AA-verified)
  warnInk: string;

  // Semantic — info
  info: string;
  infoSoft: string;
  infoSoftInk: string;
  // Solid pill ink for --info surfaces (AA-verified)
  infoInk: string;

  // Modal/drawer backdrop overlay (mid-opacity translucent)
  scrim: string;

  // Inverse surface (dark-on-light themes get dark; operator reuses bg-elev).
  // Used for toasts and other notification-style chrome.
  surfaceInverse: string;
  surfaceInverseInk: string;

  // Categorical entity-hierarchy palette (Document → Subrule).
  // Categorical, not theme-tinted; values stay constant across themes
  // unless a theme explicitly tunes them for AA contrast.
  entity1: string;
  entity2: string;
  entity3: string;
  entity4: string;
  entity5: string;
}

export interface TypographyTokens {
  fontSans: string;
  fontDisplay: string;
  fontSerif: string;
  fontMono: string;
}

export interface RadiusTokens {
  xs: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export interface ShadowTokens {
  sh1: string;
  sh2: string;
  shPop: string;
}

export interface DensityTokens {
  rowH: string;
  padX: string;
  padY: string;
  gap: string;
}

export interface LayoutTokens {
  railW: string;
  subnavW: string;
  topbarH: string;
}

export interface ThemeTokens {
  color: ColorTokens;
  typography: TypographyTokens;
  radius: RadiusTokens;
  shadow: ShadowTokens;
  density: DensityTokens;
  layout: LayoutTokens;
}

// ─────────────────────────────────────────────
// Shared tokens (same across all themes)
// ─────────────────────────────────────────────

const sharedTypography: TypographyTokens = {
  fontSans: '"Inter", system-ui, sans-serif',
  fontDisplay: '"Inter Tight", "Inter", system-ui, sans-serif',
  fontSerif: '"Source Serif 4", Georgia, serif',
  fontMono: '"JetBrains Mono", ui-monospace, monospace',
};

const sharedRadius: RadiusTokens = {
  xs: "4px",
  sm: "6px",
  md: "8px",
  lg: "12px",
  xl: "16px",
};

const defaultLayout: LayoutTokens = {
  railW: "64px",
  subnavW: "232px",
  topbarH: "56px",
};

const densityPresets: Record<DensityName, DensityTokens> = {
  compact: { rowH: "36px", padX: "12px", padY: "8px", gap: "8px" },
  medium: { rowH: "44px", padX: "16px", padY: "12px", gap: "12px" },
  comfortable: { rowH: "52px", padX: "20px", padY: "16px", gap: "16px" },
};

// ─────────────────────────────────────────────
// Palette definitions
// ─────────────────────────────────────────────

const calmColors: ColorTokens = {
  bg: "#faf7f2",
  bgElev: "#ffffff",
  bgSunken: "#f4efe8",
  bgRail: "#1f1b16",
  bgRailFg: "#c9c2b6",
  bgRailFgActive: "#faf7f2",

  ink1: "#1a1714",
  ink2: "#4a433b",
  ink3: "#7a7164",
  ink4: "#a89e8e",
  line: "#e8e1d4",
  lineStrong: "#d6ccbb",

  accent: "#b8541a",
  accentInk: "#ffffff",
  accentSoft: "#f7e8d9",
  accentSoftInk: "#8a3f12",

  ok: "#2f6a47",
  okSoft: "#dfebe2",
  okSoftInk: "#1f4a30",

  warn: "#a6691a",
  warnSoft: "#f5e7cc",
  warnSoftInk: "#6f4510",

  risk: "#a8321e",
  riskSoft: "#f2d9d2",
  riskSoftInk: "#7a2014",

  okInk: "#ffffff",
  riskInk: "#ffffff",
  warnInk: "#ffffff",

  info: "#2c5a6e",
  infoSoft: "#dbe7ec",
  infoSoftInk: "#1a3d4d",
  infoInk: "#ffffff",

  scrim: "rgba(26, 23, 20, 0.45)",

  surfaceInverse: "#1f1b16",
  surfaceInverseInk: "#faf7f2",

  entity1: "#7c3aed",
  entity2: "#2563eb",
  entity3: "#0891b2",
  entity4: "#0d9488",
  entity5: "#059669",
};

const clinicalColors: ColorTokens = {
  bg: "#f4f8f9",
  bgElev: "#ffffff",
  bgSunken: "#e6eef1",
  bgRail: "#0b2a33",
  bgRailFg: "#88b5bf",
  bgRailFgActive: "#f4f8f9",

  ink1: "#0b2a33",
  ink2: "#2a4a54",
  ink3: "#5c7480",
  ink4: "#91a4ad",
  line: "#dce6ea",
  lineStrong: "#c2d1d7",

  accent: "#0e8c9a",
  accentInk: "#ffffff",
  accentSoft: "#d6eef1",
  accentSoftInk: "#08606b",

  ok: "#1f8a6e",
  okSoft: "#d5ece4",
  okSoftInk: "#0f5e48",

  warn: "#c28a1f",
  warnSoft: "#f4e7cc",
  warnSoftInk: "#7a5810",

  risk: "#c0392b",
  riskSoft: "#f4dad6",
  riskSoftInk: "#7e2018",

  okInk: "#ffffff",
  riskInk: "#ffffff",
  warnInk: "#ffffff",

  info: "#1b6e8a",
  infoSoft: "#d4e5ec",
  infoSoftInk: "#0f4a60",
  infoInk: "#ffffff",

  scrim: "rgba(11, 42, 51, 0.45)",

  surfaceInverse: "#0b2a33",
  surfaceInverseInk: "#f4f8f9",

  entity1: "#7c3aed",
  entity2: "#2563eb",
  entity3: "#0891b2",
  entity4: "#0d9488",
  entity5: "#059669",
};

const editorialColors: ColorTokens = {
  bg: "#f6f5f2",
  bgElev: "#ffffff",
  bgSunken: "#eceae5",
  bgRail: "#14171c",
  bgRailFg: "#a8aeb8",
  bgRailFgActive: "#f6f5f2",

  ink1: "#14171c",
  ink2: "#3a4250",
  ink3: "#6b7280",
  ink4: "#9ca3af",
  line: "#e5e4df",
  lineStrong: "#d1cfc8",

  accent: "#5c2e1a",
  accentInk: "#ffffff",
  accentSoft: "#efe3dc",
  accentSoftInk: "#5c2e1a",

  ok: "#2f6a47",
  okSoft: "#dfebe2",
  okSoftInk: "#1f4a30",

  warn: "#a6691a",
  warnSoft: "#f5e7cc",
  warnSoftInk: "#6f4510",

  risk: "#a8321e",
  riskSoft: "#f2d9d2",
  riskSoftInk: "#7a2014",

  okInk: "#ffffff",
  riskInk: "#ffffff",
  warnInk: "#ffffff",

  info: "#2c5a6e",
  infoSoft: "#dbe7ec",
  infoSoftInk: "#1a3d4d",
  infoInk: "#ffffff",

  scrim: "rgba(20, 23, 28, 0.45)",

  surfaceInverse: "#14171c",
  surfaceInverseInk: "#f6f5f2",

  entity1: "#7c3aed",
  entity2: "#2563eb",
  entity3: "#0891b2",
  entity4: "#0d9488",
  entity5: "#059669",
};

const operatorColors: ColorTokens = {
  bg: "#0f1115",
  bgElev: "#161a20",
  bgSunken: "#0a0c0f",
  bgRail: "#0a0c0f",
  bgRailFg: "#6b7280",
  bgRailFgActive: "#f4d58a",

  ink1: "#f2f4f7",
  ink2: "#c8cdd6",
  ink3: "#8b95a3",
  ink4: "#5a6371",
  line: "#232830",
  lineStrong: "#2f353f",

  accent: "#f4b23a",
  accentInk: "#14110a",
  accentSoft: "#2a2316",
  accentSoftInk: "#f4d58a",

  ok: "#5bc289",
  okSoft: "#16261d",
  okSoftInk: "#8fe0b0",

  warn: "#e8b557",
  warnSoft: "#2a2316",
  warnSoftInk: "#f4d58a",

  risk: "#e47466",
  riskSoft: "#2a1715",
  riskSoftInk: "#f0a597",

  okInk: "#0f1115",
  riskInk: "#0f1115",
  warnInk: "#14110a",

  info: "#6fb7d4",
  infoSoft: "#14222a",
  infoSoftInk: "#a7d3e5",
  infoInk: "#0f1115",

  scrim: "rgba(0, 0, 0, 0.65)",

  // Operator already runs dark — reuse bgElev for consistency.
  surfaceInverse: "#161a20",
  surfaceInverseInk: "#f2f4f7",

  // Brighter ramp for legibility against the dark bg.
  entity1: "#a78bfa",
  entity2: "#60a5fa",
  entity3: "#22d3ee",
  entity4: "#2dd4bf",
  entity5: "#34d399",
};

const operatorShadows: ShadowTokens = {
  sh1: "0 1px 0 rgba(0,0,0,0.4)",
  sh2: "0 4px 12px rgba(0,0,0,0.4)",
  shPop: "0 16px 40px rgba(0,0,0,0.6)",
};

const defaultShadows: ShadowTokens = {
  sh1: "0 1px 0 rgba(26,23,20,0.04), 0 1px 2px rgba(26,23,20,0.04)",
  sh2: "0 1px 0 rgba(26,23,20,0.04), 0 4px 16px -4px rgba(26,23,20,0.08)",
  shPop: "0 12px 32px -8px rgba(26,23,20,0.18), 0 2px 6px rgba(26,23,20,0.06)",
};

// ─────────────────────────────────────────────
// Complete theme map
// ─────────────────────────────────────────────

export const themes: Record<ThemeName, ThemeTokens> = {
  calm: {
    color: calmColors,
    typography: sharedTypography,
    radius: sharedRadius,
    shadow: defaultShadows,
    density: densityPresets.medium,
    layout: defaultLayout,
  },
  clinical: {
    color: clinicalColors,
    typography: sharedTypography,
    radius: sharedRadius,
    shadow: defaultShadows,
    density: densityPresets.medium,
    layout: defaultLayout,
  },
  editorial: {
    color: editorialColors,
    // Editorial uses serif for display headings
    typography: {
      ...sharedTypography,
      fontDisplay: '"Source Serif 4", Georgia, serif',
    },
    radius: sharedRadius,
    shadow: defaultShadows,
    density: densityPresets.medium,
    layout: defaultLayout,
  },
  operator: {
    color: operatorColors,
    typography: sharedTypography,
    radius: sharedRadius,
    shadow: operatorShadows,
    // Operator defaults to compact density (dense, data-heavy)
    density: densityPresets.compact,
    layout: defaultLayout,
  },
};

export { densityPresets };

// ─────────────────────────────────────────────
// Tenant theme overrides
// ─────────────────────────────────────────────

/**
 * Subset of color tokens a tenant is permitted to override.
 * All fields are optional — only provided keys are applied.
 */
export interface TenantThemeOverride {
  accent?: string;
  accentInk?: string;
  accentSoft?: string;
  accentSoftInk?: string;
  bgRail?: string;
  bgRailFg?: string;
  bgRailFgActive?: string;
}

/**
 * Merges sanitized tenant overrides onto a base theme's color tokens.
 * Returns a new ThemeTokens — original is not mutated.
 * Values must already be sanitized (see sanitizeCssColor).
 */
export function mergeTokenOverrides(
  base: ThemeTokens,
  overrides: TenantThemeOverride,
): ThemeTokens {
  return {
    ...base,
    color: {
      ...base.color,
      ...(overrides.accent !== undefined && { accent: overrides.accent }),
      ...(overrides.accentInk !== undefined && {
        accentInk: overrides.accentInk,
      }),
      ...(overrides.accentSoft !== undefined && {
        accentSoft: overrides.accentSoft,
      }),
      ...(overrides.accentSoftInk !== undefined && {
        accentSoftInk: overrides.accentSoftInk,
      }),
      ...(overrides.bgRail !== undefined && { bgRail: overrides.bgRail }),
      ...(overrides.bgRailFg !== undefined && { bgRailFg: overrides.bgRailFg }),
      ...(overrides.bgRailFgActive !== undefined && {
        bgRailFgActive: overrides.bgRailFgActive,
      }),
    },
  };
}

// ─────────────────────────────────────────────
// CSS variable name mapping
// Converts token keys → CSS custom property names matching tokens.css
// ─────────────────────────────────────────────

export function buildCssVars(
  tokens: ThemeTokens,
  density: DensityName = "medium",
): Record<string, string> {
  const d = densityPresets[density];
  const { color: c, typography: t, radius: r, shadow: s } = tokens;

  return {
    "--bg": c.bg,
    "--bg-elev": c.bgElev,
    "--bg-sunken": c.bgSunken,
    "--bg-rail": c.bgRail,
    "--bg-rail-fg": c.bgRailFg,
    "--bg-rail-fg-active": c.bgRailFgActive,

    "--ink-1": c.ink1,
    "--ink-2": c.ink2,
    "--ink-3": c.ink3,
    "--ink-4": c.ink4,
    "--line": c.line,
    "--line-strong": c.lineStrong,

    "--accent": c.accent,
    "--accent-ink": c.accentInk,
    "--accent-soft": c.accentSoft,
    "--accent-soft-ink": c.accentSoftInk,

    "--ok": c.ok,
    "--ok-soft": c.okSoft,
    "--ok-soft-ink": c.okSoftInk,

    "--warn": c.warn,
    "--warn-soft": c.warnSoft,
    "--warn-soft-ink": c.warnSoftInk,

    "--risk": c.risk,
    "--risk-soft": c.riskSoft,
    "--risk-soft-ink": c.riskSoftInk,

    "--ok-ink": c.okInk,
    "--risk-ink": c.riskInk,
    "--warn-ink": c.warnInk,

    "--info": c.info,
    "--info-soft": c.infoSoft,
    "--info-soft-ink": c.infoSoftInk,
    "--info-ink": c.infoInk,

    "--scrim": c.scrim,

    "--surface-inverse": c.surfaceInverse,
    "--surface-inverse-ink": c.surfaceInverseInk,

    "--entity-1": c.entity1,
    "--entity-2": c.entity2,
    "--entity-3": c.entity3,
    "--entity-4": c.entity4,
    "--entity-5": c.entity5,

    "--font-sans": t.fontSans,
    "--font-display": t.fontDisplay,
    "--font-serif": t.fontSerif,
    "--font-mono": t.fontMono,

    "--r-xs": r.xs,
    "--r-sm": r.sm,
    "--r-md": r.md,
    "--r-lg": r.lg,
    "--r-xl": r.xl,

    "--sh-1": s.sh1,
    "--sh-2": s.sh2,
    "--sh-pop": s.shPop,

    "--row-h": d.rowH,
    "--pad-x": d.padX,
    "--pad-y": d.padY,
    "--gap": d.gap,

    "--rail-w": tokens.layout.railW,
    "--subnav-w": tokens.layout.subnavW,
    "--topbar-h": tokens.layout.topbarH,
  };
}
